/**
 * Created by neo on 11.01.2026
 * Image Upload Input Component - Reusable component for uploading images with preview
 */
import * as React from 'react';
import { useRef, useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Theme } from '../Theme/Theme';

export type UploadedImage = {
  file: File;
  previewUrl: string;
};

export type ImageUploadInputProps = {
  value?: UploadedImage | null;
  onChange: (image: UploadedImage | null) => void;
  maxSizeMB?: number;
  accept?: string;
  label?: string;
  error?: string;
};

const Container = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin-bottom: 8px;
`;

const UploadArea = styled.div<{ hasError?: boolean }>`
  border: 2px dashed ${(props) => (props.hasError ? Theme.Colors.error : Theme.Colors.lighterGrey)};
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
  background: ${Theme.Colors.lightestGrey};

  &:hover {
    border-color: ${Theme.Colors.primary};
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const UploadText = styled.p`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 4px 0;
`;

const UploadHint = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
  margin: 0;
`;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${Theme.Colors.error};
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: #d01535;
  }
`;

const ErrorText = styled.p`
  font-size: 12px;
  color: ${Theme.Colors.error};
  margin: 8px 0 0 0;
`;

const HiddenInput = styled.input`
  display: none;
`;

export const ImageUploadInput: React.FC<ImageUploadInputProps> = ({
  value,
  onChange,
  maxSizeMB = 5,
  accept = 'image/png,image/jpeg,image/jpg',
  label = 'Upload Image',
  error,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string>('');

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setLocalError('');

      // Validate file type
      const validTypes = accept.split(',').map((t) => t.trim());
      if (!validTypes.includes(file.type)) {
        setLocalError('Invalid file type. Please upload a PNG or JPEG image.');
        return;
      }

      // Validate file size
      const maxSizeBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        setLocalError(`File is too large. Maximum size is ${maxSizeMB}MB.`);
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);

      onChange({
        file,
        previewUrl,
      });
    },
    [accept, maxSizeMB, onChange],
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (value?.previewUrl) {
        URL.revokeObjectURL(value.previewUrl);
      }
      onChange(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [value, onChange],
  );

  const displayError = error || localError;

  return (
    <Container>
      {label && <Label>{label}</Label>}

      {value ? (
        <PreviewContainer>
          <PreviewImage src={value.previewUrl} alt="Upload preview" />
          <RemoveButton onClick={handleRemove} type="button" title="Remove image">
            ×
          </RemoveButton>
        </PreviewContainer>
      ) : (
        <UploadArea onClick={handleClick} hasError={!!displayError}>
          <UploadIcon>📷</UploadIcon>
          <UploadText>Click to upload image</UploadText>
          <UploadHint>
            PNG or JPEG, max {maxSizeMB}MB
          </UploadHint>
        </UploadArea>
      )}

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
      />

      {displayError && <ErrorText>{displayError}</ErrorText>}
    </Container>
  );
};
